import { AuthResponse, MccsRoles } from '@/@types/generated'
import { Feature } from './feature'
import { hasRole } from '@/components/Atoms/Permissions'
import { AutonomousVehiclesFeature } from './autonomous-vehicles'
import { CertificatesFeature } from './certificates'
import { InternalRoles } from '../utils'

// TX roles that can access the /insurance portal nav without also being an insurance provider.
export const SelfInsuranceTxRoles: MccsRoles[] = [
  MccsRoles.CREDENTIALING_ADMIN,
  MccsRoles.CREDENTIALING,
]

// Roles that can add/edit/clear self-insurance — Credentialing Admin only (+ Administrator via
// InsuranceFeature.Manage, checked separately), matching the backend's
// InsurancePolicyService.EnsureSelfInsuranceAuthorized. Deliberately narrower than
// SelfInsuranceTxRoles above, which also gates unrelated portal-nav visibility for plain
// Credentialing users.
export const SelfInsuranceEditRoles: MccsRoles[] = [
  MccsRoles.CREDENTIALING_ADMIN,
  MccsRoles.ADMINISTRATOR,
]

export const InsuranceFeature = {
  // Roles that have full management access (insurance providers)
  Roles: [MccsRoles.INSURANCE_PROVIDER],

  // Roles that have view-only access to insurance information
  ViewOnlyRoles: [
    MccsRoles.CREDENTIALING_ADMIN,
    MccsRoles.CREDENTIALING,
    MccsRoles.CHIEF_INVESTIGATOR,
    MccsRoles.INVESTIGATOR,
    MccsRoles.LEGAL,
    MccsRoles.MOTOR_CARRIER_OWNER,
    MccsRoles.LICENSING_AGENCY_OWNER,
    MccsRoles.LICENSING_AGENCY_USER,
  ],

  // Insurance Provider portal (/insurance) — insurance providers only
  isEnabled: (user: AuthResponse | null): boolean =>
    hasRole(InsuranceFeature.Roles, user) ||
    hasRole(SelfInsuranceTxRoles, user),

  // View insurance info (company insurance tab) — all roles with any access
  View: {
    isEnabled: (user: AuthResponse | null): boolean =>
      hasRole(
        [...InsuranceFeature.Roles, ...InsuranceFeature.ViewOnlyRoles],
        user
      ),
  } satisfies Feature,

  // Manage insurance policies (add / cancel / replace / delete)
  Manage: {
    isEnabled: (user: AuthResponse | null): boolean =>
      hasRole(InsuranceFeature.Roles, user),
  } satisfies Feature,

  // Sub-features

  Application: {
    // Anyone can apply for an insurance provider, UNLESS:
    // - They are already an AV operator
    // - They are already a motor carrier
    // - They are an internal user
    // - They are already a broker
    isEnabled: (user: AuthResponse | null): boolean =>
      !hasRole(
        [
          ...AutonomousVehiclesFeature.Roles,
          ...CertificatesFeature.Roles,
          ...InternalRoles,
          MccsRoles.BROKER,
        ],
        user
      ),
  } satisfies Feature,
} satisfies Feature
